import { useMemo } from 'react'
import { useDomainMessages } from 'domains/products/utils/domain/intl'
import { type Attribute } from 'domains/products/components/view/types/attributeOverride.types'

export enum EditAttributes {
  alcoholic = 'alcoholic',
  brandName = 'brandName',
  details = 'details',
  originalName = 'originalName',
  vegan = 'vegan',
}

export const useAttributeEditAttributes = () => {
  const messages = useDomainMessages({
    alcoholic: 'productsDomain.view.details.sections.attributes.alcoholic',
    brandName: 'productsDomain.view.details.sections.brandName',
    details: 'productsDomain.view.details.sections.details',
    productName: 'productsDomain.view.details.sections.productName',
    vegan: 'productsDomain.view.details.sections.attributes.vegan',
  })

  const attributes: { [key in EditAttributes]: Attribute } = useMemo(
    () => ({
      alcoholic: {
        name: 'alcoholic',
        displayName: messages.alcoholic,
        type: 'boolean',
        showInEdit: true,
      },
      brandName: {
        name: 'brand_name',
        displayName: messages.brandName,
        type: 'text',
        showInEdit: false,
      },
      details: {
        name: 'details',
        displayName: messages.details,
        type: 'text',
        showInEdit: true,
      },
      originalName: {
        name: 'name',
        displayName: messages.productName,
        type: 'text',
        showInEdit: true,
      },
      vegan: {
        name: 'vegan',
        displayName: messages.vegan,
        type: 'boolean',
        showInEdit: true,
      },
    }),
    [messages]
  )

  const { attributesList, booleanAttributes, attributeNames } = useMemo(
    () =>
      Object.keys(attributes).reduce(
        (acc, attributeKey) => {
          if (attributes[attributeKey].showInEdit) {
            const current = {
              label: attributes[attributeKey].displayName,
              value: attributeKey,
            }
            acc.attributesList.push(current)

            if (attributes[attributeKey].type === 'boolean') {
              acc.booleanAttributes.add(attributeKey)
            }
          }

          acc.attributeNames[attributes[attributeKey].name] = {
            displayName: attributes[attributeKey].displayName,
          }

          return acc
        },
        { attributesList: [], booleanAttributes: new Set(), attributeNames: {} }
      ),
    [attributes]
  )

  return { attributes, attributesList, booleanAttributes, attributeNames }
}
